import { useState, useEffect, FC } from 'react';
import { ApolloError } from '@apollo/client';
import { Button } from '@zoopla-eng/zds/button';
import { RadioGroup } from '@zoopla-eng/zds/radio-group';
import { triggerTracking } from '@zoopla/tracking';

import { Maybe, TrackedPropertyRelationship } from '../../graphql.generated';
import {
    getRelationshipCopy,
    mapRelationship,
    relationships
} from '@zoopla/map-relationship';
import { ControlledDialog } from '@zoopla-eng/zds/dialog';
import {
    styledErrorBannerStyle,
    styledHeading4Style,
    styledTextStyle
} from './specify-relationship-modal.styles.css';
import { fontGroups } from '@zoopla-eng/zds/themes';
import { Box } from '@zoopla-eng/zds/box';

type TrackedPropertyTracking = {
    taxonomy: {
        property_relationship?: string | null;
        uprn?: string;
        price?: Maybe<number>;
        outcode?: Maybe<string>;
        location?: Maybe<string>;
    };
};

export const SPECIFY_RELATIONSHIP_MODAL_TEXT = {
    heading: 'Set your relationship to this property',
    description: 'Get personalised information depending on your situation.',
    errorMessage:
        'Something went wrong when tracking this property. Please try again.',
    buttonText: 'Update relationship'
};

export type SpecifyRelationshipModalProps = {
    isShowing: boolean;
    action: {
        dismissRelationshipModal: () => void;
        saveAndCloseRelationshipModal: (
            relationship: TrackedPropertyRelationship
        ) => Promise<void>;
        formShownEventLabel: string;
        formShownEventCategory?: string;
        formShownEventValue?: number;
        formShownTaxonomy?: TrackedPropertyTracking['taxonomy'];
    };
    uprn?: Maybe<string>;
    existingRelationship?: TrackedPropertyRelationship | null;
    isSubmissionLoading: boolean;
    submissionError?: ApolloError;
};

const SpecifyRelationshipModal: FC<
    React.PropsWithChildren<SpecifyRelationshipModalProps>
> = ({
    isShowing,
    action: {
        dismissRelationshipModal,
        saveAndCloseRelationshipModal,
        formShownEventLabel = 'save property',
        formShownEventCategory = 'track property relationship',
        formShownEventValue,
        formShownTaxonomy
    },
    existingRelationship,
    isSubmissionLoading,
    submissionError
}) => {
    const [propertyRelationship, setPropertyRelationship] = useState<
        TrackedPropertyRelationship | undefined
    >();

    const handleChange = (relationship?: TrackedPropertyRelationship) => {
        setPropertyRelationship(relationship);
    };

    const isButtonDisabled = !propertyRelationship;

    useEffect(() => {
        if (isShowing) {
            triggerTracking({
                event: 'GAEvent',
                eventCategory: formShownEventCategory,
                eventAction: 'form shown',
                eventLabel: formShownEventLabel,
                ...(formShownEventValue && { eventValue: formShownEventValue }),
                taxonomy: formShownTaxonomy || {}
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isShowing, formShownEventLabel, formShownEventValue]);

    useEffect(() => {
        if (isShowing && existingRelationship) {
            setPropertyRelationship(existingRelationship);
        }
    }, [isShowing, existingRelationship]);

    const saveAndClose = () => {
        if (!propertyRelationship) return;
        saveAndCloseRelationshipModal(propertyRelationship);
        setPropertyRelationship(undefined);
    };

    const onClose = () => {
        dismissRelationshipModal();
        setPropertyRelationship(undefined);
    };

    const relationshipList = Object.keys(
        relationships
    ) as TrackedPropertyRelationship[];

    return (
        <ControlledDialog
            label="Property Relationship"
            isOpen={isShowing}
            onClose={onClose}
        >
            <div data-testid="property-relationship-content">
                <h4
                    className={`${styledHeading4Style} ${fontGroups.mug}`}
                    data-testid="property-relationship-heading"
                >
                    {SPECIFY_RELATIONSHIP_MODAL_TEXT.heading}
                </h4>
                <p className={`${styledTextStyle} ${fontGroups.ladle}`}>
                    {SPECIFY_RELATIONSHIP_MODAL_TEXT.description}
                </p>
                <form name="property-relationship-group">
                    <Box pb="S400">
                        {relationshipList ? (
                            <RadioGroup<TrackedPropertyRelationship | undefined>
                                id="property-relationship"
                                label="Property relationship"
                                items={relationshipList.map((relationship) => ({
                                    id: mapRelationship(
                                        relationship
                                    )?.toLocaleLowerCase(),
                                    label: getRelationshipCopy(relationship),
                                    value: relationship
                                }))}
                                onValueChange={handleChange}
                                value={propertyRelationship}
                            />
                        ) : null}
                    </Box>
                    {submissionError && (
                        <div
                            className={styledErrorBannerStyle}
                            data-testid="tracking-error-banner-modal"
                        >
                            {SPECIFY_RELATIONSHIP_MODAL_TEXT.description}
                        </div>
                    )}
                    <Button
                        data-testid="property-relationship-button"
                        category="primary"
                        fw
                        type="submit"
                        disabled={isButtonDisabled}
                        onClick={saveAndClose}
                        loading={isSubmissionLoading}
                        loadingText={SPECIFY_RELATIONSHIP_MODAL_TEXT.buttonText}
                        showSpinner
                    >
                        {SPECIFY_RELATIONSHIP_MODAL_TEXT.buttonText}
                    </Button>
                </form>
            </div>
        </ControlledDialog>
    );
};

export default SpecifyRelationshipModal;
